import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { pickBy } from 'lodash'

import {
    ChargePoint,
    FirmwareGetChargePointsByFirmwareTypeApiArg,
    useChargePointGetChargePointsQuery,
    useFirmwareGetFirmwaresByFilterQuery,
    usePartnerGetAllAllQuery,
} from '../../common/redux/api/web-api'
import { notifyError } from '../../common/features/notificationsManager/Notify'
import { compareSemanticVersions, getFirmwareVersion } from '../../common/services/utilsService'
import updateChargePointsFirmware from '../../lib/updateChargePointsFirmware'
import useText from '../../common/hooks/useText'
import { Product } from '../../support/pages/firmware-page'
import ChargePointExpandOptions from '../../lib/chargePointExpandOptions'

import canUpdateChargePoint from './canUpdateChargePoint'
import UpdateChargersFirmwareList from './updateChargersFirmwareList'
import UpdateChargersFirmwareFilters from './updateChargersFirmwareFilters'
import UpdateChargersFirmwareModal from './updateChargersFirmwareModal'

export type UpdateChargersFirmwareProps = {
    product: Partial<Product>
}

const defaultFiltersValue: FirmwareGetChargePointsByFirmwareTypeApiArg = {
    id: '',
    companyName: '',
    installedFirmwareVersion: '',
    status: undefined,
    noOfItems: 50,
    page: 0,
    partnerId: undefined,
}

export const DEFAULT_VISIBLE_ITEMS = 3

const UpdateChargersFirmwareTab: FC<UpdateChargersFirmwareProps> = ({ product }) => {
    const text = useText()
    const filtersContainerRef = useRef<HTMLDivElement | null>(null)
    const [params, setParams] = useSearchParams()
    const [filters, setFilters] = useState(Object.fromEntries(params.entries()))

    useEffect(() => {
        setFilters(Object.fromEntries(params.entries()))
    }, [params])

    const { data: firmware } = useFirmwareGetFirmwaresByFilterQuery(
        {
            productId: product.productId,
            firmwareType: product.firmwareType,
            noOfItems: DEFAULT_VISIBLE_ITEMS,
            ocppEnabled: product.ocppEnabledFirmware,
        },
        { skip: !product.productId }
    )

    const latestFirmwareVersion =
        firmware?.items && firmware?.items.length > 0
            ? firmware?.items.reduce((previousValue, currentValue) =>
                  compareSemanticVersions(previousValue.firmwareVersion ?? 0, currentValue.firmwareVersion ?? 0)
                      ? previousValue
                      : currentValue
              )
            : undefined

    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(50)
    const { data: partners } = usePartnerGetAllAllQuery({ excludeOrphans: false })
    const {
        data: chargePointsList,
        isFetching: chargePointsIsLoading,
        refetch: refetchChargers,
        error: chargePointsError,
    } = useChargePointGetChargePointsQuery(
        {
            ...filters,
            productId: product.productId,
            firmwareType: product.ocppEnabledFirmware ? undefined : product.firmwareType,
            ocppOnly: product.ocppEnabledFirmware,
            page: currentPage,
            status: filters.status,
            noOfItems: pageSize,
            expand: ChargePointExpandOptions.OcppConfig
        },
        { skip: !product.productId }
    )

    const [chargePoints, setChargePoints] = useState<ChargePoint[]>([])
    const [confirmUpdateChargers, setConfirmUpdateChargers] = useState(false)
    const [sort, setSort] = useState<{ by?: string; direction?: 'asc' | 'desc' }>({})

    const partnerOptions = useMemo(
        () => [
            { value: undefined, label: 'Any partner' },
            ...(partners || []).map((x) => ({ value: x.id, label: x.name })),
        ],
        [partners]
    )

    const handlePageClick = (selected: number) => {
        window.scrollTo(0, filtersContainerRef.current?.offsetTop || 0)
        setCurrentPage(selected)
    }
    const pageClick = () => {
        setCurrentPage(0)
    }

    const onSelectCharger = (id: string, checked: boolean) => {
        setChargePoints(chargePoints?.map((cp) => (cp.id === id ? { ...cp, checked } : cp)))
    }

    const onSelectAllChargers = (checked: boolean) => {
        const selectedChargePoints = chargePoints?.map((chargePoint) => {
            const latestFirmwareVersionNotInstalled =
                latestFirmwareVersion?.firmwareVersion !== chargePoint.installedFirmwareVersion ||
                compareSemanticVersions(
                    latestFirmwareVersion?.firmwareVersion ?? 0,
                    getFirmwareVersion(chargePoint.ocppConfigKeys)
                )

            return latestFirmwareVersionNotInstalled && canUpdateChargePoint(chargePoint)
                ? { ...chargePoint, checked }
                : chargePoint
        })

        setChargePoints(selectedChargePoints)
    }

    const submitUpdateFirmware = (firmwareId: number) => {
        const updateModel = {
            firmwareId,
            chargePointIds: chargePoints.filter((x) => x.checked).map((x) => x.id),
        }

        return updateChargePointsFirmware(updateModel)
    }

    useEffect(() => {
        if (chargePointsError) notifyError(text('msgFirmwareLoadError'))
    }, [chargePointsError])

    useEffect(() => {
        setChargePoints(chargePointsList?.items ?? [])
    }, [chargePointsList])

    const onChargePointFiltersChange = (fieldName: string, value: unknown) => {
        const params = {
            [fieldName]: value,
            ...(fieldName !== 'page' && { page: 0 }),
        }
        updateFilters(params)
    }

    const updateFilters = (filterParams) => {
        const newFilter = {
            ...filters,
            ...filterParams,
        }
        const params = pickBy(newFilter, (value) => value !== undefined && value !== null)
        setParams(params)
    }

    const clearFilters = () => {
        updateFilters(defaultFiltersValue)
    }

    return (
        <>
            <UpdateChargersFirmwareFilters
                filters={filters}
                productName={product.title}
                partnerOptions={partnerOptions}
                onChange={onChargePointFiltersChange}
                onClearFilters={clearFilters}
                onSubmit={(filter) => {
                    updateFilters(filter)
                    setCurrentPage(0)
                }}
                pageClick={pageClick}
                isFetching={chargePointsIsLoading}
            />

            <UpdateChargersFirmwareList
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                latestFirmwareVersion={latestFirmwareVersion?.firmwareVersion}
                handlePageClick={handlePageClick}
                onSelect={onSelectCharger}
                onSelectAll={onSelectAllChargers}
                totalCount={chargePointsList?.totalCount ?? 0}
                filters={params}
                onUpdate={() => setConfirmUpdateChargers(true)}
                onChangeSort={(by, direction) => setSort({ by, direction })}
                sortBy={sort.by}
                sortDirection={sort.direction}
                chargePoints={chargePoints}
                setSelectedPageSize={setPageSize}
                isPending={chargePointsIsLoading}
            />

            {confirmUpdateChargers && (
                <UpdateChargersFirmwareModal
                    productId={product.productId}
                    firmwareType={product.firmwareType}
                    firmwareTitle={product.firmwareType}
                    chargePoints={chargePoints.filter((chargePoint) => chargePoint.checked)}
                    productTitle={product.title}
                    onSubmit={submitUpdateFirmware}
                    onCancel={(refresh) => {
                        setConfirmUpdateChargers(false)
                        if (refresh) {
                            refetchChargers()
                        }
                    }}
                />
            )}
        </>
    )
}

export default memo(UpdateChargersFirmwareTab)
